// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/starship-stars.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// starship-stars.ts — STARSHIP's ground: a field of small, hard, IN-FOCUS,
// ACHROMATIC points translating as one rigid body along a fixed heading. The
// ship is under way. Nothing rotates, nothing blurs, nothing pulses.
//
// IT OWNS NO CANVAS AND NO LOOP. PR-4's style-axis.ts owns the element, the
// ONE-canvas cap, the rAF and pause-on-hidden; this module registers a
// PAINTER (`(ctx, t) => void`) and is called once per frame with the
// substrate's clock. That split is what puts the field inside
// scripts/perf-budget.mjs's cap and pause arms — a style running its own
// loop would be outside every one of them.
//
// WHY IT CANNOT BE MISTAKEN FOR GLASS'S BOKEH (cross-style ruling R2, four
// simultaneous axes): the element is a ~1px HARD POINT, not an 80-240px
// defocused disc; it is IN FOCUS always; it is ACHROMATIC — every point is
// `--ink`, one hue for the entire field, never a theme-tinted accent; and
// the motion is ONE RIGID TRANSLATION of the whole field, not per-element
// sine drift. Ruling R13 adds the fourth: this is motion in XY across the
// picture plane, where Synthwave's is recession along Z.
//
// THE ALPHA CEILING IS THE CONTRACT WITH THE CONTRAST GATE.
// `--ss-star-alpha-near` is declared in styles.css, composited under every
// tier fill by scripts/style-contrast.mjs's ground arm, and CLAMPED here for
// every layer. The gate measures a number the painter provably cannot
// exceed — which is the only reason a translucent card tier over a live
// field can have a meaningful contrast floor at all.
//
// DEEP FIELD'S DENSER FIELD, WITHOUT A PER-THEME TABLE. The spec asks Deep
// Field for 1.6x the count and 1.25x the alpha. A theme writes only the 22
// UI tokens onto <html>, so there is no seat for a per-theme STYLE token and
// inventing one is a substrate change, not a style PR. Instead the boost is
// derived from the ground itself: the darker the ground, the more of the
// field is above the noise floor. That is better physics than a table and it
// applies to any future STARSHIP theme for free. Measured: Void Watch
// (--bg #060A12) gets 1.00x, Deep Field (#020304) 1.43x density / 1.18x
// alpha, and a pure black ground would reach the spec's 1.6x / 1.25x caps.
//
// PER-FRAME COST, AND THE DEVIATION FROM THE SPEC'S PRE-RENDER. The spec
// asks for three OffscreenCanvas layers pre-rendered at 2x viewport and
// blitted. At dpr 2 on a 1440x900 window that is a 5760x3600 backing store
// PER LAYER — ~83 MB of texture memory for a background, and it also makes
// the spec's own "wrap with a random re-seed of the perpendicular
// coordinate" impossible, because a pre-rendered tile can only repeat. This
// paints instead with ONE batched path fill per layer: 3 fill() calls and
// ~450 rect() ops per frame, no arc() anywhere (that is the call the spec
// was actually refusing), and zero extra memory.

import { STARSHIP } from './styles.js';
import { applyStyleBackground, backgroundCanvasCount, effectiveGrade, teardownStyleBackground } from './style-axis.js';

/** The hard ceiling on any star's alpha, mirrored here so a stylesheet that
 * loses the token cannot silently raise it. `--ss-star-alpha-near` is the
 * live value; this is what it is clamped to.
 *
 * MEASURED, NOT CHOSEN. The spec's near-layer alpha was .55. Composited by
 * scripts/style-contrast.mjs under the modal tier — the tier with the least
 * fill between the text and the ground — it put tertiary text at 2.03 on
 * Void Watch, 2.36 on Deep Field and 2.25 on Day Watch, all below the 2.5
 * floor. The ceiling ships at .40 with the tier fills raised to 90/86/84,
 * which measures 2.73 worst-case: 9% of headroom over the floor. */
export const STAR_ALPHA_CEILING = 0.4;

/** The spec's Deep Field multipliers, as CAPS on the darkness boost. */
export const DENSITY_BOOST_CAP = 1.6;
export const ALPHA_BOOST_CAP = 1.25;

/** MEASURED relative luminance of Void Watch's `--bg` (#060A12) — the
 * reference ground at which the boost is exactly 1.0x. Any ground at or
 * above it gets the plain field; below it the field densifies. */
export const REFERENCE_GROUND_LUMINANCE = 0.003;

/** How often the painter re-reads layout and theme tokens (ms). Both are
 * getComputedStyle / clientWidth reads: cheap, but not 60 times a second. */
const REFRESH_MS = 500;

/** dpr is capped: a 3x backing store for 1px points is pure cost. */
const MAX_DPR = 2;

/** The heading, normalised. A shallow down-left drift — the same vector for
 * every layer, which is what makes the field read as ONE body in motion
 * rather than as particles. */
const HEADING = ((): { x: number; y: number } => {
  const x = -0.94;
  const y = 0.34;
  const m = Math.hypot(x, y);
  return { x: x / m, y: y / m };
})();

interface LayerSpec {
  /** parallax depth name — used in the gate/smoke output */
  depth: 'far' | 'mid' | 'near';
  /** star count at boost 1.0 */
  count: number;
  /** half-width of the drawn square, CSS px (a 1px point is a 1px point; an
   * arc() would cost 60x for the same pixels) */
  r: number;
  /** the CSS custom property carrying this depth's alpha */
  alphaToken: string;
  /** CSS px per second along the heading. `far` is static: parallax at
   * infinity does not move. */
  speed: number;
}

/** Three parallax depths (spec section 4). Fixed, not random. */
export const STAR_LAYERS: LayerSpec[] = [
  { depth: 'far', count: 260, r: 0.5, alphaToken: '--ss-star-alpha-far', speed: 0 },
  { depth: 'mid', count: 140, r: 0.9, alphaToken: '--ss-star-alpha-mid', speed: 1.2 },
  { depth: 'near', count: 50, r: 1.4, alphaToken: '--ss-star-alpha-near', speed: 3.0 },
  // The three alphas (.16 / .28 / .40 in styles.css) are the spec's
  // .22/.38/.55 ladder scaled by the factor the contrast gate allowed — the
  // depth separation is proportional, only the ceiling moved.
];

interface Star {
  x: number;
  y: number;
}
interface Layer {
  spec: LayerSpec;
  stars: Star[];
  fill: string;
}
interface Env {
  at: number;
  w: number;
  h: number;
  dpr: number;
  layers: Layer[];
}

let env: Env | null = null;
let lastTick = -1;

const clamp = (n: number, lo: number, hi: number): number => Math.min(Math.max(n, lo), hi);

const channel = (c: number): number => {
  const v = clamp(c, 0, 255) / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};

/** Relative luminance of a resolved `#rrggbb` / `rgb()` colour. Returns the
 * reference luminance for anything it cannot parse, which makes an
 * unparseable ground a boost of exactly 1.0 rather than a surprise. */
export function groundLuminance(value: string): number {
  const v = value.trim().toLowerCase();
  let r: number | null = null;
  let g = 0;
  let b = 0;
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/.exec(v);
  if (hex) {
    const raw = hex[1] ?? '';
    const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
    r = parseInt(full.slice(0, 2), 16);
    g = parseInt(full.slice(2, 4), 16);
    b = parseInt(full.slice(4, 6), 16);
  } else {
    const rgb = /^rgba?\(\s*([0-9.]+)[\s,]+([0-9.]+)[\s,]+([0-9.]+)/.exec(v);
    if (rgb) {
      r = parseFloat(rgb[1] ?? '0');
      g = parseFloat(rgb[2] ?? '0');
      b = parseFloat(rgb[3] ?? '0');
    }
  }
  if (r === null) return REFERENCE_GROUND_LUMINANCE;
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * The darkness boost, 0..1, from the resolved ground colour. 0 at (or above)
 * Void Watch's luminance, 1 at a pure black ground.
 */
export function darknessBoost(bg: string): number {
  const lum = groundLuminance(bg);
  return clamp((REFERENCE_GROUND_LUMINANCE - lum) / REFERENCE_GROUND_LUMINANCE, 0, 1);
}

/** `#rrggbb` + alpha -> rgba(). Anything else is returned unchanged and the
 * caller's globalAlpha carries the fade. */
function withAlpha(color: string, a: number): string {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  const h = m?.[1];
  if (!h) return color;
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return `rgba(${parseInt(full.slice(0, 2), 16)}, ${parseInt(full.slice(2, 4), 16)}, ${parseInt(full.slice(4, 6), 16)}, ${a})`;
}

/** Is the painted app mode LIGHT? A lit bridge has no visible stars — the
 * painter draws nothing, and starship-stars' own ground sync tears the
 * canvas down entirely so no rAF runs either. Belt AND braces: the teardown
 * is the mechanism, this is the guarantee if a caller ever grounds the
 * document without going through it. */
function lightMode(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.getAttribute('data-theme') === 'light';
}

function seed(count: number, w: number, h: number): Star[] {
  const out: Star[] = [];
  for (let i = 0; i < count; i++) out.push({ x: Math.random() * w, y: Math.random() * h });
  return out;
}

function readEnv(canvas: HTMLCanvasElement, now: number): Env {
  const cs = getComputedStyle(document.documentElement);
  const tok = (name: string, fallback: string): string => cs.getPropertyValue(name).trim() || fallback;
  const ink = tok('--ink', '#E6EDF6');
  const boost = darknessBoost(tok('--bg', '#060A12'));
  const density = 1 + (DENSITY_BOOST_CAP - 1) * boost;
  const alphaScale = 1 + (ALPHA_BOOST_CAP - 1) * boost;
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  const dpr = Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, MAX_DPR);
  const want = { w: Math.round(w * dpr), h: Math.round(h * dpr) };
  if (canvas.width !== want.w || canvas.height !== want.h) {
    canvas.width = want.w;
    canvas.height = want.h;
  }
  const resized = !env || env.w !== w || env.h !== h;
  const layers: Layer[] = STAR_LAYERS.map((spec, i) => {
    const declared = parseFloat(tok(spec.alphaToken, '0'));
    const base = Number.isFinite(declared) ? declared : 0;
    // The near layer's declared alpha IS the ceiling the contrast gate
    // measured, so the boost may lift the dimmer depths toward it and never
    // past it.
    const alpha = clamp(base * alphaScale, 0, STAR_ALPHA_CEILING);
    const count = Math.round(spec.count * density);
    const prev = env?.layers[i];
    let stars = prev && !resized ? prev.stars : seed(count, w, h);
    if (stars.length > count) stars = stars.slice(0, count);
    else if (stars.length < count) stars = [...stars, ...seed(count - stars.length, w, h)];
    return { spec, stars, fill: withAlpha(ink, alpha) };
  });
  return { at: now, w, h, dpr, layers };
}

/**
 * THE painter. Registered against the STARSHIP style id; the substrate calls
 * it once per animation frame with a monotonic clock.
 */
export function paintStarfield(ctx: CanvasRenderingContext2D, t: number): void {
  const canvas = ctx.canvas as HTMLCanvasElement;
  if (lightMode()) return;
  if (!env || t - env.at > REFRESH_MS || t < env.at) env = readEnv(canvas, t);
  const { w, h, dpr, layers } = env;

  // dt is CLAMPED: the substrate pauses on a hidden tab, so the first frame
  // after a return carries an arbitrarily large gap. Without the clamp the
  // field would teleport, which is the one thing a rigid translation must
  // never do.
  const dt = lastTick < 0 || t < lastTick ? 0 : Math.min(t - lastTick, 100);
  lastTick = t;

  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  for (const layer of layers) {
    const { r, speed } = layer.spec;
    if (speed > 0 && dt > 0) {
      const dx = HEADING.x * speed * (dt / 1000);
      const dy = HEADING.y * speed * (dt / 1000);
      const m = r + 1;
      for (const s of layer.stars) {
        s.x += dx;
        s.y += dy;
        // Wrap on the viewport edge with a RANDOM RE-SEED of the
        // perpendicular coordinate, so the field never visibly repeats.
        if (s.x < -m) {
          s.x = w + m;
          s.y = Math.random() * h;
        } else if (s.y > h + m) {
          s.y = -m;
          s.x = Math.random() * w;
        }
      }
    }
    // ONE batched path per depth: 3 fill() calls per frame for the whole
    // field, and not one arc().
    ctx.fillStyle = layer.fill;
    ctx.beginPath();
    const d = r * 2;
    for (const s of layer.stars) ctx.rect(s.x - r, s.y - r, d, d);
    ctx.fill();
  }
}

/** Test seam — the smoke drives the painter across arms. */
export function resetStarfield(): void {
  env = null;
  lastTick = -1;
}

/** What the painter is currently drawing. Read by the smoke so the counts it
 * prints are the counts that render, not the counts in a comment. */
export function starfieldStats(): { depth: string; count: number; fill: string }[] {
  return (env?.layers ?? []).map((l) => ({ depth: l.spec.depth, count: l.stars.length, fill: l.fill }));
}

// ---------------------------------------------------------------------
// THE LIGHT SLOT — no element, no rAF
// ---------------------------------------------------------------------
//
// "A lit bridge has no visible stars" (spec section 4): Day Watch renders NO
// canvas at all, and specifically not a paused one. The substrate grades the
// ground per STYLE, not per style x mode, so this is the one seam a style
// has to reach for — and it reaches through the substrate's own exported
// API (applyStyleBackground / teardownStyleBackground), never around it, so
// the ONE-canvas cap and the pause-on-hidden wiring still own the element.
//
// Called from style-runtime.ts onStylePainted, which runs on EVERY repaint.
// It is therefore written as a converge-to-desired-state function with an
// early return when nothing needs to change: mounting and unmounting a
// canvas on every store emit would be its own kind of defect.

/**
 * Converge the starfield ground on the painted mode. Returns what it did, so
 * a caller can log it and the smoke can assert both directions.
 *
 *   'not-starship' — another style is painted; its own ground already stands
 *   'unchanged'    — the ground already matches the mode
 *   'mounted'      — dark mode, canvas built through the substrate
 *   'torn-down'    — light mode, element and rAF gone
 */
export function syncStarfieldGround(styleId: string): 'not-starship' | 'unchanged' | 'mounted' | 'torn-down' {
  if (typeof document === 'undefined') return 'not-starship';
  if (styleId !== STARSHIP.id) return 'not-starship';
  // Under ANY reduce lane effectiveGrade() degrades `canvas` to `gradient` and
  // the element is never constructed — so "wanted" is false there too, and
  // this function must not fight the viewer's preference by rebuilding on
  // every repaint.
  //
  // That now includes the app's own `[data-reduce="1"]` switch: effectiveGrade()
  // honours all three reduce lanes at the substrate, so the old
  // `!effectsReduced()` OR (a workaround for the gap that switch used to fall
  // through, when effectiveGrade consulted the two OS queries alone) is
  // retired. The substrate tears STARSHIP's field down — element and loop —
  // for every lane rather than leaving a running rAF behind a hidden canvas.
  const wanted = !lightMode() && effectiveGrade(STARSHIP.background) === 'canvas';
  const have = backgroundCanvasCount() > 0;
  if (wanted === have) return 'unchanged';
  if (wanted) {
    applyStyleBackground(STARSHIP);
    return 'mounted';
  }
  teardownStyleBackground();
  resetStarfield();
  return 'torn-down';
}
